/**
 * NIGHTBOUND - desktop top navigation.
 *
 * Left: the wordmark. Centre: the map of the world. Right: who you are and how
 * to leave. Hidden below `lg`, where the bottom bar takes over.
 */

import { NavLink } from 'react-router-dom'
import { GAME_NAV } from '../nav'
import { useGame } from '../state/GameProvider'
import { audio } from '../audio/AudioManager'
import { Chip, cx } from './ui'
import { Logo } from './Logo'

export function TopNav() {
  const { user, creature, logout, updateSettings } = useGame()
  const soundOn = user?.settings.soundEnabled ?? true

  const toggleSound = () => {
    const next = !soundOn
    audio.setEnabled(next)
    void updateSettings({ soundEnabled: next })
  }

  return (
    <header className="sticky top-0 z-30 hidden border-b border-ash-700/40 bg-night-950/85 backdrop-blur lg:block">
      <div className="mx-auto flex h-14 max-w-[1800px] items-center gap-6 px-5">
        <NavLink to="/shelter" className="shrink-0">
          <Logo size="sm" />
        </NavLink>

        <nav aria-label="Primary" className="flex flex-1 items-center gap-1">
          {GAME_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cx(
                  'group relative rounded-[3px] px-3 py-2 transition-colors',
                  isActive ? 'bg-night-800/80' : 'hover:bg-night-800/50',
                )
              }
            >
              {({ isActive }) => (
                <span className="flex items-center gap-2">
                  <span aria-hidden className="text-sm text-ash-400 group-hover:text-bone-300">
                    {item.glyph}
                  </span>
                  <span
                    className={cx(
                      'nb-label text-[9.5px]',
                      isActive ? 'text-ember-400' : 'text-ash-300 group-hover:text-bone-300',
                    )}
                  >
                    {item.label}
                  </span>
                  {item.locked ? (
                    <span className="nb-label text-[8px] text-ash-500">soon</span>
                  ) : null}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-3">
          {creature ? (
            <Chip tone="neutral">
              <span aria-hidden>◆</span>
              Lv {creature.level}
            </Chip>
          ) : null}
          <span className="hidden text-xs text-ash-300 xl:inline">{user?.username}</span>
          <button
            type="button"
            onClick={toggleSound}
            aria-pressed={soundOn}
            aria-label={soundOn ? 'Mute sound' : 'Unmute sound'}
            className="rounded-[3px] border border-ash-700/60 px-2 py-1.5 text-xs text-ash-300 hover:bg-night-800 hover:text-bone-200"
          >
            <span aria-hidden>{soundOn ? '🔊' : '🔇'}</span>
            <span className="ml-1.5 nb-label text-[9px]">{soundOn ? 'Sound on' : 'Muted'}</span>
          </button>
          <button
            type="button"
            onClick={() => void logout()}
            className="rounded-[3px] border border-ash-700/60 px-3 py-1.5 nb-label text-[9px] text-ash-300 hover:bg-night-800 hover:text-bone-200"
          >
            Leave
          </button>
        </div>
      </div>
    </header>
  )
}
